import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ─── Sentry — must initialise BEFORE any React render so ErrorBoundary
//     and global error hooks can plug in. No-op if DSN env var unset.
import { initSentry } from "@/lib/sentryInit";
import AppErrorBoundary from "@/components/AppErrorBoundary";
initSentry();

// ─── Console noise filter ─────────────────────────────────────────────────
// Recharts emits a "width(-1) and height(-1) of chart should be greater than 0"
// warning during its very first measurement pass before the DOM has settled.
// The chart still renders correctly on the next pass — the warning is purely
// cosmetic and clutters the console for users opening DevTools. Filter it.
if (typeof window !== "undefined") {
  const origWarn = console.warn;
  console.warn = (...args) => {
    const msg = String(args[0] || "");
    if (
      msg.includes("width(-1)") ||
      msg.includes("height(-1)") ||
      (msg.includes("ResponsiveContainer") && msg.includes("greater than 0"))
    ) {
      return; // swallow Recharts first-paint noise
    }
    origWarn.apply(console, args);
  };
}

// ─── Clipboard error filter ──────────────────────────────────────────────
// navigator.clipboard.writeText() rejects in sandboxed iframes (preview
// environments) and any context that lost user-gesture focus. Many copy
// buttons across the app fire this without explicit .catch() — which
// surfaces as the red React dev-overlay 'Uncaught runtime errors' covering
// the whole page. Swallow ONLY those specific rejections (everything else
// still bubbles to Sentry/console as expected).
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev?.reason;
    const msg = String(reason?.message || reason || "");
    const isClipboard = (
      reason?.name === "NotAllowedError" ||
      msg.includes("Clipboard") ||
      msg.includes("clipboard") ||
      msg.includes("Document is not focused") ||
      msg.includes("write permission denied") ||
      msg.includes("Write permission denied")
    );
    if (isClipboard) {
      ev.preventDefault();
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);
